// Attendance data layer — teacher + admin + parent.
//
// The backing migration (20260701000000) writes one row per child per
// day with a status enum. Teacher/admin call `rpc_mark_attendance`
// which upserts + auto-stamps check-in/out. Parent gets a SELECT-only
// view of their own children's rows.

import { supabase, isSupabaseConfigured } from "./supabase";

function ensureLive() {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
}

// ---------------------------------------------------------------------------
// Status metadata shared by teacher + admin UI
// ---------------------------------------------------------------------------

export const ATTENDANCE_STATUSES = [
  { value: "present",    label: "Present",    short: "P",  tone: "primary" },
  { value: "late",       label: "Late",       short: "L",  tone: "accent" },
  { value: "absent",     label: "Absent",     short: "A",  tone: "muted" },
  { value: "excused",    label: "Excused",    short: "E",  tone: "muted" },
  { value: "left_early", label: "Left early", short: "LE", tone: "accent" },
];

export const STATUS_META = {
  present:    { label: "Present",    tone: "border-primary/30 bg-primary/10 text-primary-hover" },
  late:       { label: "Late",       tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" },
  absent:     { label: "Absent",     tone: "border-neutral-200 bg-neutral-100 text-neutral-600" },
  excused:    { label: "Excused",    tone: "border-neutral-200 bg-neutral-100 text-neutral-500" },
  left_early: { label: "Left early", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#C6851E]" },
  unmarked:   { label: "Not yet marked", tone: "border-dashed border-neutral-300 bg-neutral-50 text-neutral-500" },
};

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Teacher — today's roster
// ---------------------------------------------------------------------------

/**
 * Returns one row per active child in the teacher's assigned
 * classrooms, left-joined with any attendance record for the given
 * date. If no record exists, the row's `attendance` is null —
 * rendered as "Not yet marked" in the UI.
 */
export async function listRosterForTeacher({ date, profileId }) {
  ensureLive();
  const d = date || todayStr();

  // 1) Teacher's active classroom assignments today.
  const { data: assigns, error: aerr } = await supabase
    .from("staff_assignments")
    .select("classroom_id, ends_on")
    .eq("profile_id", profileId)
    .or(`ends_on.is.null,ends_on.gte.${d}`);
  if (aerr) throw aerr;
  const classroomIds = Array.from(
    new Set((assigns || []).map((a) => a.classroom_id).filter(Boolean)),
  );
  if (classroomIds.length === 0) return { classrooms: [], rows: [] };

  // 2) Classrooms + active children per classroom + today's attendance.
  const { data: rooms, error: rerr } = await supabase
    .from("classrooms")
    .select(`
      id, name, age_group, capacity, required_staff_ratio,
      children:children!inner(id, first_name, last_name, withdrawn_at)
    `)
    .in("id", classroomIds)
    .is("children.withdrawn_at", null)
    .order("name");
  if (rerr) throw rerr;

  const childIds = [];
  for (const r of rooms || []) for (const c of r.children || []) childIds.push(c.id);

  let records = [];
  if (childIds.length > 0) {
    let { data: recs, error: recErr } = await supabase
      .from("attendance_records")
      .select(`id, child_id, status, check_in_at, check_out_at, notes, marked_by, updated_at,
        pickup_self, pickup_authorization_id,
        pickup_authorization:pickup_authorizations(id, full_name)`)
      .in("child_id", childIds)
      .eq("date", d);
    // Pre-pickup-audit migration fallback.
    if (recErr && (recErr.code === "42703" || /column .* does not exist/i.test(recErr.message || ""))) {
      ({ data: recs, error: recErr } = await supabase
        .from("attendance_records")
        .select("id, child_id, status, check_in_at, check_out_at, notes, marked_by, updated_at")
        .in("child_id", childIds)
        .eq("date", d));
    }
    if (recErr) throw recErr;
    records = recs || [];
  }
  const byChild = new Map(records.map((r) => [r.child_id, r]));

  const rows = [];
  for (const room of rooms || []) {
    for (const c of room.children || []) {
      rows.push({
        child_id: c.id,
        child_first_name: c.first_name,
        child_last_name: c.last_name,
        classroom_id: room.id,
        classroom_name: room.name,
        attendance: byChild.get(c.id) || null,
      });
    }
  }
  rows.sort((a, b) =>
    (a.classroom_name || "").localeCompare(b.classroom_name || "")
    || (a.child_first_name || "").localeCompare(b.child_first_name || ""),
  );
  return { classrooms: rooms || [], rows };
}

// ---------------------------------------------------------------------------
// Admin — day view with classroom KPIs + ratio evaluation
// ---------------------------------------------------------------------------

/**
 * Full day snapshot for admin. One row per classroom with:
 *   - enrolled children
 *   - present / late / absent / excused counts
 *   - active staff assigned AS OF that date (any assignment without
 *     an end_on OR end_on>=date counts)
 *   - ratio health flag (healthy | near | over | none)
 * Plus the raw rows so admin can drill into individual children.
 */
export async function adminDaySnapshot(date) {
  ensureLive();
  const d = date || todayStr();

  const { data: rooms, error: rerr } = await supabase
    .from("classrooms")
    .select(`
      id, name, age_group, capacity, required_staff_ratio,
      children:children(id, first_name, last_name, withdrawn_at),
      assignments:staff_assignments(id, profile_id, starts_on, ends_on,
        profile:profiles(id, full_name))
    `)
    .order("name");
  if (rerr) throw rerr;

  const childIds = [];
  for (const r of rooms || []) {
    for (const c of r.children || []) {
      if (!c.withdrawn_at) childIds.push(c.id);
    }
  }

  let records = [];
  if (childIds.length > 0) {
    let { data: recs, error: recErr } = await supabase
      .from("attendance_records")
      .select(`id, child_id, classroom_id, status, check_in_at, check_out_at, notes, marked_by, updated_at,
        pickup_self, pickup_authorization_id,
        pickup_authorization:pickup_authorizations(id, full_name)`)
      .in("child_id", childIds)
      .eq("date", d);
    if (recErr && (recErr.code === "42703" || /column .* does not exist/i.test(recErr.message || ""))) {
      ({ data: recs, error: recErr } = await supabase
        .from("attendance_records")
        .select("id, child_id, classroom_id, status, check_in_at, check_out_at, notes, marked_by, updated_at")
        .in("child_id", childIds)
        .eq("date", d));
    }
    if (recErr) throw recErr;
    records = recs || [];
  }
  const byChild = new Map(records.map((r) => [r.child_id, r]));

  const classrooms = (rooms || []).map((room) => {
    const active = (room.children || []).filter((c) => !c.withdrawn_at);
    const staffAssigned = (room.assignments || []).filter(
      (a) => (!a.ends_on || a.ends_on >= d) && (!a.starts_on || a.starts_on <= d),
    );
    const rowsForRoom = active.map((c) => ({
      child: c,
      attendance: byChild.get(c.id) || null,
    }));
    const counts = rowsForRoom.reduce(
      (acc, r) => {
        const s = r.attendance?.status || "unmarked";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      { present: 0, late: 0, absent: 0, excused: 0, left_early: 0, unmarked: 0 },
    );
    // "Effective present" = present + late — these need supervision.
    const effectivePresent = (counts.present || 0) + (counts.late || 0);
    const ratio = room.required_staff_ratio;
    // We have `staffAssigned` as of `date`, but we don't model per-day
    // staff attendance (staff can be out). Use assignment count as a
    // proxy; a future iteration can add staff-check-in.
    const staffCount = staffAssigned.length;
    let health = "none";
    if (ratio && staffCount > 0 && effectivePresent > 0) {
      const maxChildren = staffCount * ratio;
      if (effectivePresent > maxChildren) health = "over";
      else if (effectivePresent >= Math.floor(maxChildren * 0.85)) health = "near";
      else health = "healthy";
    } else if (ratio && effectivePresent > 0 && staffCount === 0) {
      // Kids present, no staff on paper = over ratio
      health = "over";
    }
    return {
      ...room,
      active_children_count: active.length,
      rows: rowsForRoom,
      counts,
      effective_present: effectivePresent,
      staff_assigned: staffAssigned,
      staff_count: staffCount,
      ratio,
      health,
    };
  });

  return { date: d, classrooms };
}

// ---------------------------------------------------------------------------
// Mutations — used by both teacher + admin.
// ---------------------------------------------------------------------------

export async function markAttendance({ childId, date, status, notes }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_mark_attendance", {
    p_child_id: childId,
    p_date: date || todayStr(),
    p_status: status,
    p_notes: notes ?? null,
  });
  if (error) {
    // Module C — hard-enforcement gate surfaces as P0403. Wrap the
    // Supabase error into a structured object so callers can render a
    // calm blocking dialog instead of a toast with a raw Postgres
    // message. Teachers see the blocker labels + an admin-call hint.
    if (error.code === "P0403" || /Blocked by compliance/i.test(error.message || "")) {
      const err = new Error(error.message || "Blocked by compliance");
      err.code = "P0403";
      err.kind = "compliance_block";
      err.blockerSummary = (error.message || "").replace(/^Blocked by compliance:\s*/i, "");
      throw err;
    }
    throw error;
  }
  if (Array.isArray(data)) return data[0] || null;
  return data;
}

/** Module C — admin-only override. Requires a non-empty reason. */
export async function markAttendanceOverride({ childId, date, status, reason, notes }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_mark_attendance_override", {
    p_child_id: childId,
    p_date: date || todayStr(),
    p_status: status,
    p_reason: reason,
    p_notes: notes ?? null,
  });
  if (error) throw error;
  if (Array.isArray(data)) return data[0] || null;
  return data;
}

export async function stampPickup({ childId, date, pickupAuthorizationId = null, pickupSelf = false }) {
  ensureLive();
  const { data, error } = await supabase.rpc("rpc_stamp_pickup", {
    p_child_id: childId,
    p_date: date || todayStr(),
    p_pickup_authorization_id: pickupAuthorizationId,
    p_pickup_self: pickupSelf,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data;
}

// ---------------------------------------------------------------------------
// Teacher history + admin CSV export helpers.
// ---------------------------------------------------------------------------

export async function listRecentAttendance({ childId, since }) {
  ensureLive();
  const { data, error } = await supabase
    .from("attendance_records")
    .select("id, child_id, date, status, check_in_at, check_out_at, notes")
    .eq("child_id", childId)
    .gte("date", since)
    .order("date", { ascending: false });
  if (error) throw error;
  return data || [];
}

/**
 * Parent-facing pickup history: rows for a family's children where
 * a pickup actually happened (check_out_at IS NOT NULL). RLS already
 * scopes results to children the caller is on file for. Returns the
 * audit fields so the UI can render "by {Name|parent}".
 */
export async function listFamilyPickupHistory({ childIds, limit = 30 }) {
  ensureLive();
  if (!childIds?.length) return [];
  let { data, error } = await supabase
    .from("attendance_records")
    .select(`id, child_id, date, check_in_at, check_out_at,
      pickup_self, pickup_authorization_id,
      pickup_authorization:pickup_authorizations(id, full_name, photo_path),
      child:children(id, first_name, last_name, profile_photo_path)`)
    .in("child_id", childIds)
    .not("check_out_at", "is", null)
    .order("date", { ascending: false })
    .limit(limit);
  // Pre-pickup-audit migration tolerance.
  if (error && (error.code === "42703" || /column .* does not exist/i.test(error.message || ""))) {
    ({ data, error } = await supabase
      .from("attendance_records")
      .select(`id, child_id, date, check_in_at, check_out_at,
        child:children(id, first_name, last_name, profile_photo_path)`)
      .in("child_id", childIds)
      .not("check_out_at", "is", null)
      .order("date", { ascending: false })
      .limit(limit));
  }
  if (error) throw error;
  return data || [];
}

// ---------------------------------------------------------------------------
// "Yesterday's leftovers" — children who were checked in but never
// checked out on a recent past day. RLS scopes the query to whatever
// the caller can already see (teacher: own classrooms; admin: all org).
// ---------------------------------------------------------------------------

/**
 * Returns prior-day attendance rows that have a check-in but no
 * check-out. Used by the teacher attendance page (and admin) to surface
 * forgotten pick-up stamps before they pollute the audit trail.
 *
 *   `since` — ISO date string, exclusive of today. Defaults to T-3 days
 *             back so a long weekend doesn't leave a stale list.
 *   `today` — pass-through so callers can use a stable `todayStr()`.
 */
export async function listLeftovers({ since, today } = {}) {
  ensureLive();
  const t = today || todayStr();
  const sinceDate = since || isoDateOffset(t, -3);
  const { data, error } = await supabase
    .from("attendance_records")
    .select(`
      id, child_id, date, status, check_in_at, check_out_at, classroom_id,
      child:children(id, first_name, last_name),
      classroom:classrooms(id, name)
    `)
    .gte("date", sinceDate)
    .lt("date", t)
    .not("check_in_at", "is", null)
    .is("check_out_at", null)
    .in("status", ["present", "late", "left_early"])
    .order("date", { ascending: false });
  if (error) throw error;
  return data || [];
}

function isoDateOffset(iso, days) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function formatLeftoverDate(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * Pulls every record for a date range + classroom filter. Admin-only
 * (parent + teacher RLS will naturally scope rows down).
 */
export async function listRecordsForRange({ from, to, classroomId }) {
  ensureLive();
  let q = supabase
    .from("attendance_records")
    .select(`
      id, date, status, check_in_at, check_out_at, notes,
      classroom_id, classroom:classrooms(id, name),
      child:children(id, first_name, last_name, family:families(id, display_name))
    `)
    .gte("date", from)
    .lte("date", to)
    .order("date", { ascending: false });
  if (classroomId) q = q.eq("classroom_id", classroomId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export function rowsToCsv(rows) {
  const header = [
    "date", "child_first_name", "child_last_name", "family", "classroom",
    "status", "check_in_at", "check_out_at", "notes",
  ];
  const escape = (s) => {
    if (s == null) return "";
    const str = String(s);
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push([
      r.date,
      r.child?.first_name,
      r.child?.last_name,
      r.child?.family?.display_name,
      r.classroom?.name,
      r.status,
      r.check_in_at || "",
      r.check_out_at || "",
      r.notes || "",
    ].map(escape).join(","));
  }
  return lines.join("\n");
}

export function formatTime(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Autofill helper — "who has a daily report today but isn't marked present?"
// ---------------------------------------------------------------------------

/**
 * For the given list of child ids, return a Set of ids that already
 * have at least one daily_report row for `date`. Teachers use this to
 * offer a one-tap 'Mark present' shortcut for kids who've clearly
 * shown up today (since a report was logged against them).
 *
 * Teacher RLS on daily_reports is already restricted to their assigned
 * classrooms, so we don't have to duplicate permission logic here.
 */
export async function listChildrenWithReport({ childIds, date }) {
  ensureLive();
  if (!Array.isArray(childIds) || childIds.length === 0) return new Set();
  const d = date || todayStr();
  const { data, error } = await supabase
    .from("daily_reports")
    .select("child_id")
    .in("child_id", childIds)
    .eq("report_date", d);
  if (error) throw error;
  return new Set((data || []).map((r) => r.child_id));
}
